import React from 'react';

/**
 * RmsGaugeChart
 * -------------
 * A semi-circular gauge chart component purpose-built for the RMS
 * Risk Stratification views (e.g. RmsPatientOverview, RmsTriageProforma).
 * Self-contained — no runtime dependencies outside of React.
 *
 * Props:
 *   - percentage: number (0-100) - Fallback for needle position if riskLevel not provided
 *   - riskLevel: string - Risk level text (e.g., 'Urgent', 'High', 'Medium', 'Standard')
 *   - size: number - Overall size of the gauge (default: 120)
 *   - segmentColor: string - Color for gauge segments (default: '#ffc2c2')
 *   - captionText: string - Text displayed below the gauge
 *   - showCaption: boolean - Whether to show caption (default: true)
 *   - thickness: number - Stroke width of gauge segments (default: 36)
 *   - captionColor: string - Color for caption text (default: '#FF3B3B')
 *
 * Visual Design:
 *   - Four segments representing Standard, Medium, High, Urgent risk levels
 *   - Needle points to the appropriate segment based on riskLevel
 *   - White separators between segments for clarity
 *   - Brand-colored needle and center cap (#43213e)
 */
const RmsGaugeChart = ({
  percentage,
  riskLevel,
  size = 120,
  segmentColor = '#ffc2c2',
  captionText = 'High Priority',
  showCaption = true,
  thickness = 36,
  captionColor = '#FF3B3B',
}) => {
  const strokeWidth = thickness;
  const radius = (size - thickness) / 2;

  const cx = size / 2;
  const cy = size / 2;

  /**
   * Converts polar coordinates (angle in degrees, radius) to Cartesian (x, y).
   * Angle 0 is at 12 o'clock, positive angles go clockwise.
   */
  const polar = (angleDeg, r) => {
    const a = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };

  /**
   * Creates an SVG arc path from startDeg to endDeg.
   */
  const arcPath = (startDeg, endDeg) => {
    const start = polar(endDeg, radius);
    const end = polar(startDeg, radius);
    const largeArc = endDeg - startDeg <= 180 ? 0 : 1;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`;
  };

  /**
   * Maps risk level string to needle angle.
   * Segments: Standard (-90 to -45), Medium (-45 to 0), High (0 to 45), Urgent (45 to 90)
   * Needle points to middle of each segment.
   * 
   * Risk levels (consistent terminology): Urgent, High, Medium, Standard
   */
  const levelToAngle = (level) => {
    if (!level) return null;
    const s = String(level).toLowerCase();
    // Urgent -> 4th segment (rightmost)
    if (s.includes('urgent')) return 67.5;
    // High -> 3rd segment
    if (s.includes('high')) return 22.5;
    // Medium -> 2nd segment
    if (s.includes('medium')) return -22.5;
    // Standard/Low -> 1st segment (leftmost)
    if (s.includes('standard') || s.includes('low')) return -67.5;
    return null;
  };

  // Calculate needle angle from risk level, fallback to percentage
  const mappedAngle = levelToAngle(riskLevel || captionText);
  const needleAngle =
    mappedAngle !== null
      ? mappedAngle
      : -90 + (Math.max(0, Math.min(percentage || 0, 100)) / 100) * 180;
  const needleLen = radius * 0.8;
  const needleEnd = polar(needleAngle, needleLen);
  
  /**
   * Creates a tapered needle path that is wider at the base and narrows to
   * a rounded point at the tip. The needle shape looks like a elongated teardrop.
   * 
   * @param {number} baseWidth - Width of the needle at the base (near center)
   * @param {number} tipRadius - Radius for the rounded tip
   */
  const createTaperedNeedlePath = (baseWidth = 16, tipRadius = 6) => {
    // Calculate perpendicular angle for base width
    const perpAngle = needleAngle + 90;
    const perpRad = (perpAngle - 90) * Math.PI / 180;
    
    // Base points (perpendicular to needle direction at center)
    const baseOffset = baseWidth / 2;
    const baseLeft = {
      x: cx + baseOffset * Math.cos(perpRad),
      y: cy + baseOffset * Math.sin(perpRad),
    };
    const baseRight = {
      x: cx - baseOffset * Math.cos(perpRad),
      y: cy - baseOffset * Math.sin(perpRad),
    };
    
    // Tip point
    const tip = needleEnd;
    
    // Create a path that tapers from base to tip with a rounded end
    // Using quadratic curves for smooth tapering
    return `
      M ${baseLeft.x} ${baseLeft.y}
      Q ${(baseLeft.x + tip.x) / 2} ${(baseLeft.y + tip.y) / 2} ${tip.x} ${tip.y}
      Q ${(baseRight.x + tip.x) / 2} ${(baseRight.y + tip.y) / 2} ${baseRight.x} ${baseRight.y}
      Z
    `;
  };
  
  // Scale needle dimensions based on gauge size
  const needleBaseWidth = Math.max(12, size * 0.08);
  const centerCapRadius = Math.max(7, size * 0.045);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 10}>
        {/* Four segments - same color, representing risk scale */}
        <path
          d={arcPath(-90, -45)}
          stroke={segmentColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="butt"
        />
        <path
          d={arcPath(-45, 0)}
          stroke={segmentColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="butt"
        />
        <path
          d={arcPath(0, 45)}
          stroke={segmentColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="butt"
        />
        <path
          d={arcPath(45, 90)}
          stroke={segmentColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="butt"
        />

        {/* White separators between segments */}
        {[-45, 0, 45].map((deg) => (
          <line
            key={deg}
            x1={polar(deg, radius - strokeWidth / 2).x}
            y1={polar(deg, radius - strokeWidth / 2).y}
            x2={polar(deg, radius + strokeWidth / 2).x}
            y2={polar(deg, radius + strokeWidth / 2).y}
            stroke="#FFFFFF"
            strokeWidth={6}
          />
        ))}

        {/* Tapered needle - wider at base, tapering to rounded point */}
        <path
          d={createTaperedNeedlePath(needleBaseWidth)}
          fill="#43213e"
          stroke="#43213e"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Center cap - covers needle base */}
        <circle cx={cx} cy={cy} r={centerCapRadius} fill="#43213e" />
      </svg>

      {/* Caption below gauge */}
      {showCaption && (
        <div
          className="text-lg font-extrabold text-center mt-1 whitespace-nowrap"
          style={{ color: captionColor }}
        >
          {captionText}
        </div>
      )}
    </div>
  );
};

export default RmsGaugeChart;
